//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { UserMultiple, Connect, EventSchedule } from '@carbon/icons-react';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLoopContext } from '../../LoopContext';
import { useActiveLoopContext } from '../../ActiveLoopContext';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useUserAuth } from '../../UserAuth';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RightPanel from './RightPanel';
import EditModal from './Edit/EditModal';
import WebhooksTab from './Webhooks/WebhooksTab';
import WikiTab from './Wiki/WikiTab';
import { useTranslation } from 'react-i18next';
import TenantDataService from '../../utils/TenantDataService';
import LoopDataService from '../../utils/LoopDataService';
import { useConfig } from '../../Config';
import {
  fetchWikiStatus,
  getCachedWikiStatus,
} from '../../utils/wikiStatusCache';
import successAlertIcon from '../../icons/success-alert.svg';
import errorAlertIcon from '../../icons/error-alert.svg';

// Constants for infinite scroll
const BATCH_SIZE = 10; // Load 10 segments at a time
const LOAD_DELAY = 300; // Small delay for loading animation

interface LoopResource {
  id?: string;
  type?: string; // e.g. TEST_PROJECT / CONTROL_REPOSITORY
  name?: string;
  description?: string;
  link?: string;
  creator?: { fullName?: string };
  created?: string; // ISO timestamp
}

interface LoopSegment {
  id?: string;
  displayName?: string;
  name?: string;
  description?: string;
  solution?: string;
  url?: string;
  createdBy?: string;
  dateCreated?: string;
  creator?: string;
  creatorUsername?: string;
  resources?: LoopResource[];
  completed?: boolean;
}

interface TaskStatusMap {
  [type: string]: string;
}

interface UserInfo {
  username: string;
  fullname: string;
  email?: string;
}

interface LoopDetails {
  id: string;
  description: string;
  resources: LoopResource[];
  members?: UserInfo[];
}

export const getEmptyLoopData = (id: string) => ({
  id,
  description: '',
  resources: [],
});

interface LoopMemberApi {
  userId: string;
  email: string;
  fullName: string | null;
  role: 'ROLE_ADMIN' | 'ROLE_USER' | string;
  // backend supplies array of loop names
  otherLoops?: string[];
}

const LoopDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { loopId } = useParams();
  const navigate = useNavigate();
  const { selectedLoop, setSelectedLoop } = useLoopContext();
  const {
    setLoopContext,
    activeLoopId,
    activeLoopName,
    activeTeamspaceId,
    clearLoopContext,
  } = useActiveLoopContext();
  const location = useLocation();
  const passedAssociatedUserIds: string[] | undefined = (location as any)?.state
    ?.associatedUserIds;
  const { teamspaceId, teamspaceName } = useTeamspaceContext();
  const { user, isAuthenticated, licenses } = useUserAuth();
  const { solutions } = useConfig() as {
    solutions?: { name: string; path: string }[];
  };
  const controlBasePath = (
    solutions?.find((s) => s.name === 'Control')?.path ?? ''
  ).replace(/\/$/, '');
  const loopName = selectedLoop?.name ?? activeLoopName ?? '';
  const [activeTab, setActiveTab] = useState<
    'connections' | 'members' | 'webhooks' | 'wiki'
  >('connections');

  // Initialize loopDetails immediately to prevent flicker on page reload.
  // If selectedLoop exists (from context or will be restored from sessionStorage),
  // create initial empty data structure. This prevents the "loading-loop-details"
  // flash that occurs when loopDetails is null during the first render.
  const [loopDetails, setLoopDetails] = useState<LoopDetails | null>(() => {
    if (selectedLoop) {
      return getEmptyLoopData(selectedLoop.id);
    }
    // Check sessionStorage for persisted loop data on page reload
    const savedLoop = sessionStorage.getItem('selectedLoop');
    if (savedLoop) {
      try {
        const parsedLoop = JSON.parse(savedLoop);
        return getEmptyLoopData(parsedLoop.id);
      } catch {
        // Invalid JSON, will be handled by useEffect
      }
    }
    return null;
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [rightPanelType, setRightPanelType] = useState<
    'repository' | 'segment' | 'project' | 'segmentProgress' | null
  >(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<UserInfo[]>([]);
  const [rawLoopMembers, setRawLoopMembers] = useState<LoopMemberApi[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'success' | 'error'>('success');

  const [isTeamspaceAdmin, setIsTeamspaceAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Local state holding mapped associated users from loop metadata
  const [loopAssociatedUsers, setLoopAssociatedUsers] = useState<UserInfo[]>(
    [],
  );
  const [loopResources, setLoopResources] = useState<LoopResource[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [segmentStatusByType, setSegmentStatusByType] = useState<
    Record<string, string | null>
  >({});
  const [enabledTaskTypes, setEnabledTaskTypes] = useState<
    {
      displayName: string;
      type: string;
      description: string;
    }[]
  >([]);
  const [segmentCompleted, setSegmentCompleted] = useState<boolean | null>(
    null,
  );
  const [currentSegmentId, setCurrentSegmentId] = useState<string | null>(null);
  const [loopSegments, setLoopSegments] = useState<LoopSegment[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [segmentsError, setSegmentsError] = useState<string | null>(null);
  const [segmentCreationError, setSegmentCreationError] = useState<
    string | null
  >(null);
  const [segmentsWithTasks, setSegmentsWithTasks] = useState<LoopSegment[]>([]);
  // Track active resource creation tasks to show placeholder rows while polling
  const [activeResourceTasks, setActiveResourceTasks] = useState<
    { selfHref: string; taskType: string; displayName: string }[]
  >([]);
  const [resolveIncompleteSegment, setResolveIncompleteSegment] =
    useState<boolean>(false);
  const [segmentFailed, setSegmentFailed] = useState<boolean>(false);
  const [expandedSegments, setExpandedSegments] = useState<
    Record<string, boolean>
  >({});

  const toggleSegment = (segmentKey: string) => {
    setExpandedSegments((prev) => ({
      ...prev,
      [segmentKey]: !prev[segmentKey],
    }));
  };

  // Guard: if the persisted loop context belongs to a different teamspace,
  // clear stale context and redirect to homepage to prevent cross-teamspace
  // data leaks (e.g. refreshing /loop-details after switching teamspaces).
  useEffect(() => {
    if (teamspaceId && activeTeamspaceId && activeTeamspaceId !== teamspaceId) {
      clearLoopContext();
      setSelectedLoop(null);
      sessionStorage.removeItem('selectedLoop');
      navigate('/', { replace: true });
    }
  }, [
    teamspaceId,
    activeTeamspaceId,
    clearLoopContext,
    setSelectedLoop,
    navigate,
  ]);

  // Set active loop context when LoopDetailsPage is opened
  useEffect(() => {
    const effectiveLoopId = loopId || selectedLoop?.id;
    if (effectiveLoopId && teamspaceId) {
      // Use selectedLoop.name if available, otherwise use effectiveLoopId as fallback
      const loopName = selectedLoop?.name || effectiveLoopId;
      setLoopContext(effectiveLoopId, loopName, teamspaceId);
    }
  }, [
    loopId,
    selectedLoop?.id,
    selectedLoop?.name,
    teamspaceId,
    setLoopContext,
  ]);

  // Infinite scroll state for segments
  const [visibleSegmentCount, setVisibleSegmentCount] = useState(BATCH_SIZE);
  const [loadingMoreSegments, setLoadingMoreSegments] = useState(false);
  const segmentSentinelRef = useRef<HTMLDivElement | null>(null);

  // Memoize service so its reference stays stable and does not retrigger effects every render
  const loopDataService = useMemo(
    () =>
      new LoopDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  // Wiki availability is checked eagerly per loop (once per loop, cached) so
  // the wiki tab button itself can be hidden when the loop has no wiki,
  // rather than only hiding the tab's content after it's opened.
  const [loopWikiFetchResult, setLoopWikiFetchResult] = useState<
    { loopId: string; hasWiki: boolean } | undefined
  >(undefined);
  const currentLoopIdForWiki = selectedLoop?.id;
  const cachedLoopHasWiki = currentLoopIdForWiki
    ? getCachedWikiStatus(`loop:${currentLoopIdForWiki}`)
    : undefined;
  // Derived during render; the effect below only setState()s from its async callback.
  const loopHasWiki =
    cachedLoopHasWiki ??
    (loopWikiFetchResult && loopWikiFetchResult.loopId === currentLoopIdForWiki
      ? loopWikiFetchResult.hasWiki
      : undefined);

  useEffect(() => {
    const currentLoopId = currentLoopIdForWiki;
    if (
      !currentLoopId ||
      !teamspaceId ||
      getCachedWikiStatus(`loop:${currentLoopId}`) !== undefined
    ) {
      return;
    }
    const cacheKey = `loop:${currentLoopId}`;
    let cancelled = false;
    fetchWikiStatus(cacheKey, () =>
      loopDataService
        .fetchALoop(teamspaceId, currentLoopId)
        .then((loop) => ({ hasWiki: !!loop?.hasWiki })),
    ).then((result) => {
      if (!cancelled) {
        setLoopWikiFetchResult({ loopId: currentLoopId, hasWiki: result });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentLoopIdForWiki, teamspaceId, loopDataService]);

  // If the active tab is 'wiki' but this loop turns out to have no wiki, fall back.
  useEffect(() => {
    if (activeTab === 'wiki' && loopHasWiki === false) {
      setActiveTab('connections');
    }
  }, [activeTab, loopHasWiki]);

  const handleEdit = (resource: string) => {
    // Placeholder retained intentionally; edit functionality not yet implemented.
  };

  const handleDelete = (resource: string) => {
    // Placeholder retained intentionally; delete functionality not yet implemented.
  };

  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  // Define refreshLoops outside of useEffect
  const mapRole = (role: string) =>
    role === 'ROLE_ADMIN'
      ? 'ADMINISTRATOR'
      : role === 'ROLE_USER'
        ? 'TEAM MEMBER'
        : role;

  const refreshMembers = useCallback(async () => {
    if (!selectedLoop || !teamspaceId) return;
    setMembersLoading(true);
    setMembersError(null);
    try {
      const resp = await loopDataService.getLoopMembers(
        teamspaceId,
        selectedLoop.id,
      );
      // resp assumed to be an array of LoopMemberApi
      const arr: LoopMemberApi[] = Array.isArray(resp) ? resp : [];
      setRawLoopMembers(arr);
      const mapped: UserInfo[] = arr.map((m) => ({
        username: m.userId,
        fullname: m.fullName || m.email || m.userId,
        email: m.email,
      }));
      setTeamMembers(mapped);
    } catch (e: any) {
      setMembersError(t('failed-to-load-members') || 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
    // `t` is intentionally omitted: react-i18next can hand back a new `t`
    // identity on renders unrelated to translation state, and including it
    // here caused this callback (and the effect that calls it) to refire on
    // every such render — retriggering the members fetch in a tight loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoop, teamspaceId, loopDataService]);

  // Combined refresh for members table AND loop metadata (associatedUserIds)
  // This is passed to the EditModal so that after progress completes we re-sync
  // the authoritative metadata before the modal is closed/re-opened.
  const refreshLoopData = async () => {
    await refreshMembers();
    if (teamspaceId && selectedLoop?.id) {
      try {
        const meta = await loopDataService.fetchALoop(
          teamspaceId,
          selectedLoop.id,
        );
        const ids: string[] = Array.isArray(meta?.associatedUserIds)
          ? meta.associatedUserIds
          : [];
        setLoopAssociatedUsers(ids.map((u) => ({ username: u, fullname: u })));
      } catch (_) {
        // silent; keep previous associated users if metadata fetch fails
      }
    }
  };

  // For modal: fetch all teamspace members so user can add/remove beyond current loop membership.
  // We do NOT pass them directly as associatedUsers; associatedUsers remains loop members only
  // so modal can distinguish currently selected vs available to add.
  const prepareModalMembers = async () => {
    if (!teamspaceId) return;
    try {
      await tenantDataService.fetchTeamspaceMembers(teamspaceId);
    } catch (_) {
      // swallow errors; modal will handle its own fetch
    }
  };

  const showSnackbar = useCallback((message: string, type?: string) => {
    setAlertMessage(message);
    setAlertType(type === 'error' ? 'error' : 'success');
    setAlertOpen(true);

    // Auto-close after 10 seconds
    setTimeout(() => {
      setAlertOpen(false);
    }, 10000);
  }, []);

  const fetchingResourcesRef = useRef(false);
  const fetchLoopResources = useCallback(async () => {
    if (!teamspaceId || !selectedLoop?.id) return;
    if (fetchingResourcesRef.current) return; // skip if already in flight
    fetchingResourcesRef.current = true;
    setResourcesLoading(true);
    try {
      const resp = await loopDataService.getLoopResources(
        teamspaceId,
        selectedLoop.id,
      );
      const resources = Array.isArray(resp?.resources) ? resp.resources : [];
      setLoopResources(resources);
      setLoopDetails((prev) =>
        prev ? { ...prev, resources } : getEmptyLoopData(selectedLoop.id),
      );
      if (Array.isArray(resp?.errors) && resp.errors.length) {
        // Collect string representations of errors
        const msg = resp.errors
          .map((e: any) =>
            typeof e === 'string' ? e : e?.message || JSON.stringify(e),
          )
          .join(' | ');
        // Show in alert box (error style) but also keep table data
        showSnackbar(`Resource errors: ${msg}`, 'error');
      }
    } catch (e) {
      setLoopResources([]);
      setLoopDetails((prev) =>
        prev ? { ...prev, resources: [] } : getEmptyLoopData(selectedLoop.id),
      );
      // Show error in alert box instead of table
      const errorMessage =
        e instanceof Error ? e.message : 'Failed to load resources';
      showSnackbar(errorMessage, 'error');
    } finally {
      fetchingResourcesRef.current = false;
      setResourcesLoading(false);
    }
  }, [teamspaceId, selectedLoop?.id, loopDataService, showSnackbar]);

  const fetchLoopSegments = useCallback(async () => {
    if (!teamspaceId || !selectedLoop?.id) return;
    setSegmentsLoading(true);
    setSegmentsError(null);
    try {
      const segments = await loopDataService.getLoopSegments(
        teamspaceId,
        selectedLoop.id,
      );
      setLoopSegments(Array.isArray(segments) ? segments : []);

      // Fetch loop members to map creator IDs to usernames
      let loopMembers: LoopMemberApi[] = [];
      try {
        const membersResp = await loopDataService.getLoopMembers(
          teamspaceId,
          selectedLoop.id,
        );
        loopMembers = Array.isArray(membersResp) ? membersResp : [];
      } catch (err) {
        console.error('Failed to fetch loop members for creator mapping:', err);
      }

      // Fetch resources for each segment and resolve creator username
      const segmentsWithTasksData = await Promise.all(
        (Array.isArray(segments) ? segments : []).map(async (segment: any) => {
          let resources = [];
          let creatorUsername = '';

          if (segment.id) {
            try {
              const resourcesData = await loopDataService.getSegmentResources(
                teamspaceId,
                selectedLoop.id,
                segment.id,
              );
              // Use resources from the response
              resources = Array.isArray(resourcesData?.resources)
                ? resourcesData.resources
                : [];
            } catch (err) {
              console.error(
                `Failed to fetch resources for segment ${segment.id}:`,
                err,
              );
            }
          }

          // Map creator ID (Keycloak ID) to username using loop members
          if (segment.creator) {
            const member = loopMembers.find(
              (m) => m.userId === segment.creator,
            );
            if (member) {
              creatorUsername =
                member.fullName || member.email || member.userId;
            } else {
              // If not found in loop members, show the creator ID
              creatorUsername = segment.creator;
            }
          }

          return { ...segment, resources, creatorUsername };
        }),
      );
      setSegmentsWithTasks(segmentsWithTasksData);
    } catch (err) {
      console.error('Failed to fetch loop segments:', err);
      setLoopSegments([]);
      setSegmentsWithTasks([]);
      setSegmentsError('Failed to load segments');
    } finally {
      setSegmentsLoading(false);
    }
  }, [teamspaceId, selectedLoop?.id, loopDataService]);

  // Initialize loop details & members; resources fetched separately
  // Initialize basic loop details & members when loop changes
  useEffect(() => {
    if (selectedLoop) {
      setLoopDetails(getEmptyLoopData(selectedLoop.id));
      if (Array.isArray(passedAssociatedUserIds)) {
        setLoopAssociatedUsers(
          passedAssociatedUserIds.map((u) => ({ username: u, fullname: u })),
        );
      }
    } else {
      setLoopDetails(getEmptyLoopData('default'));
    }
  }, [loopId, selectedLoop, passedAssociatedUserIds]);

  // Restore selectedLoop from sessionStorage on page refresh
  useEffect(() => {
    if (!selectedLoop) {
      const savedLoop = sessionStorage.getItem('selectedLoop');
      if (savedLoop) {
        try {
          const parsedLoop = JSON.parse(savedLoop);
          setSelectedLoop(parsedLoop);
        } catch (error) {
          console.error(
            'Failed to parse saved loop from sessionStorage:',
            error,
          );
        }
      }
    } else {
      // Save selectedLoop to sessionStorage whenever it changes
      sessionStorage.setItem('selectedLoop', JSON.stringify(selectedLoop));
    }
  }, [selectedLoop, setSelectedLoop]);

  // Fetch members when tab switched to members or loop changes
  useEffect(() => {
    if (activeTab === 'members') {
      refreshMembers();
    }
  }, [activeTab, selectedLoop?.id, teamspaceId, refreshMembers]);

  useEffect(() => {
    if (activeTab === 'connections') {
      fetchLoopResources();
      fetchLoopSegments();
    }
  }, [activeTab, fetchLoopResources, fetchLoopSegments]);

  // Reset visible segment count when segments change
  useEffect(() => {
    setVisibleSegmentCount(BATCH_SIZE);
  }, [segmentsWithTasks.length]);

  // Default-expand all segments when they load
  useEffect(() => {
    setExpandedSegments((prev) => {
      const next = { ...prev };
      let changed = false;
      segmentsWithTasks.forEach((seg) => {
        const key = seg.id || seg.displayName || seg.name || '';
        if (key && next[key] === undefined) {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [segmentsWithTasks]);

  // IntersectionObserver for infinite scroll of segments
  useEffect(() => {
    if (!segmentSentinelRef.current) return;
    if (visibleSegmentCount >= segmentsWithTasks.length) return; // all loaded

    let timeoutId: number | undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          setLoadingMoreSegments(true);
          timeoutId = window.setTimeout(() => {
            setVisibleSegmentCount((prev) =>
              Math.min(prev + BATCH_SIZE, segmentsWithTasks.length),
            );
            setLoadingMoreSegments(false);
          }, LOAD_DELAY);
        }
      },
      { root: null, rootMargin: '100px', threshold: 0 },
    );
    observer.observe(segmentSentinelRef.current);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [segmentsWithTasks.length, visibleSegmentCount]);

  // Check if the user is a teamspace admin
  useEffect(() => {
    const fetchTeamspaceUserRole = async () => {
      try {
        if (teamspaceId && user?.profile?.sub) {
          const roleResp = await tenantDataService.getUserTeamspaceRole(
            teamspaceId,
            user?.profile?.sub,
          );
          if (typeof roleResp === 'string') {
            setIsTeamspaceAdmin(roleResp === 'ROLE_ADMIN');
          } else if ('status' in roleResp && roleResp.status === 204) {
            // 204 status means the user could not be found
            setIsTeamspaceAdmin(false);
          }
        } else {
          setIsTeamspaceAdmin(false);
        }
      } catch (e) {
        console.error(
          "An error occurred retrieving the user's teamspace role: ",
          e,
        );
        setIsTeamspaceAdmin(false);
      }
    };
    fetchTeamspaceUserRole();
  }, [teamspaceId, user?.profile?.sub, tenantDataService]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Periodically check segment completion when segment progress panel is open
  useEffect(() => {
    if (
      rightPanelType !== 'segmentProgress' ||
      !currentSegmentId ||
      segmentCompleted
    ) {
      return;
    }

    const checkCompletion = async () => {
      if (!teamspaceId || !selectedLoop?.id || !currentSegmentId) return;

      try {
        const segmentData = await loopDataService.fetchHref(
          `/spaces/${teamspaceId}/loops/${selectedLoop.id}/segments/${currentSegmentId}`,
        );
        if (segmentData?.completed === true) {
          // Guard: only mark completed when local task tracking agrees
          const allLocalTasksDone =
            Object.keys(segmentStatusByType).length > 0 &&
            Object.values(segmentStatusByType).every((s) => s === 'COMPLETED');
          if (allLocalTasksDone) {
            setSegmentCompleted(true);
          }
        }
      } catch (e) {
        console.error('Failed to check segment completion', e);
      }
    };

    // Check immediately
    checkCompletion();

    // Then check every 3 seconds
    const intervalId = setInterval(checkCompletion, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    rightPanelType,
    currentSegmentId,
    segmentCompleted,
    segmentStatusByType,
    teamspaceId,
    selectedLoop?.id,
    loopDataService,
  ]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Create array of licensed products (similar to DropdownMenu)
  const LicenseApp = licenses.flatMap((license) =>
    license.split(',').map((item: string) => item.trim().toLowerCase()),
  );

  const mapResourceType = (type?: string) => {
    if (!type) return '';
    const beforeUnderscore = type.split('_')[0];
    return beforeUnderscore.toUpperCase();
  };

  // Check if user has license for a given resource type
  const hasLicenseForResource = (resourceType?: string): boolean => {
    if (!resourceType) return false;
    const productName = resourceType.split('_')[0].toLowerCase();
    return LicenseApp.includes(productName);
  };

  // TODO: The code below is temporarily commented out because testhib's OAuth2
  // callback misparsed the origin URL when loopId/loopName/teamspaceId were
  // appended as query params (the & separators were not URL-encoded), resulting
  // in a broken Spring Security auth flow with an invalid redirect_uri.
  // Once testhub makes changes on their side to correctly handle the loop
  // context we are providing (loopId, loopName, teamspaceId), this can be
  // uncommented and the workaround below removed.
  //
  // const buildResourceLink = (link: string): string => {
  //   if (!activeLoopId || !activeLoopName) return link;
  //   try {
  //     const url = new URL(link, window.location.origin);
  //     url.searchParams.set('loopId', activeLoopId);
  //     url.searchParams.set('loopName', activeLoopName);
  //     if (activeTeamspaceId) {
  //       url.searchParams.set('teamspaceId', activeTeamspaceId);
  //     }
  //     return url.toString();
  //   } catch {
  //     return link;
  //   }
  // };

  // Workaround: return the resource link as-is from the backend until testhub
  // supports receiving loop context via URL query params.
  const buildResourceLink = (link: string): string => {
    return link ?? '';
  };

  const formatResourceName = (res: LoopResource) => {
    const base = res.name || '';
    if (!base || !res.type) return base;

    const parts = res.type.split('_');
    if (parts.length > 1) {
      const afterUnderscore = parts.slice(1).join('_').replace(/_/g, ' ');
      return (
        base +
        ' ' +
        afterUnderscore.charAt(0).toUpperCase() +
        afterUnderscore.slice(1).toLowerCase()
      );
    }
    return base;
  };

  const [activeNavTab, setActiveNavTab] = useState<'home' | 'details'>(
    'details',
  );

  const renderActionButton = () => {
    if (activeTab === 'members') {
      return null;
    } else {
      // Show Add Connection dropdown for connections tab
      return isTeamspaceAdmin ? (
        <div
          id="id-loop-details-action-dropdown"
          data-testid="loop-details-action-dropdown"
          className="relative"
          ref={dropdownRef}
        >
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm text-primary-foreground transition-colors hover:bg-primary-hover"
          >
            {t('add-connection')}
            <ArrowDropDownIcon
              className={`text-xl transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {dropdownOpen && (
            <div
              id="id-loop-details-dropdown-menu"
              data-testid="loop-details-dropdown-menu"
              className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-card py-2 shadow-lg"
            >
              <button
                className="w-full px-4 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary"
                onClick={() => {
                  setRightPanelType('repository');
                  setRightPanelOpen(true);
                  setDropdownOpen(false);
                }}
              >
                {t('control-repo')}
              </button>
              <button
                className="w-full px-4 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary"
                onClick={() => {
                  setRightPanelType('segment');
                  setRightPanelOpen(true);
                  setDropdownOpen(false);
                }}
              >
                {t('loop-segment')}
              </button>
              <button
                className="w-full px-4 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary"
                onClick={() => {
                  setRightPanelType('project');
                  setRightPanelOpen(true);
                  setDropdownOpen(false);
                }}
              >
                {t('test-project')}
              </button>
            </div>
          )}
        </div>
      ) : null;
    }
  };

  if (!selectedLoop) {
    return (
      <div
        id="id-loop-details-no-loop-selected"
        data-testid="loop-details-no-loop-selected"
        className="p-8 text-center text-muted-foreground"
      >
        {t('no-selected-loop')}
      </div>
    );
  }

  if (!loopDetails) {
    return (
      <div
        id="id-loop-details-loading"
        data-testid="loop-details-loading"
        className="p-8 text-center text-muted-foreground"
      >
        {t('loading-loop-details')}
      </div>
    );
  }

  const handleCloseRightPanel = () => {
    if (rightPanelType === 'segmentProgress') {
      setSegmentStatusByType({});
      setSegmentCompleted(null);
    }
    setRightPanelOpen(false);
    setRightPanelType(null);
    // Refresh segments when panel closes, similar to how resources refresh
    fetchLoopSegments();
  };

  const pollSegmentTaskStatus = async (
    selfHref: string,
    taskType: string,
    maxAttempts: number = 30,
    interval: number = 3000,
  ) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const resp = await fetch(selfHref, {
          headers: {
            Authorization: 'Bearer ' + (user?.access_token || ''),
            Accept: 'application/hal+json',
          },
        });
        if (!resp.ok) throw new Error('HTTP ' + resp.status);

        const json = await resp.json();
        const status = json?.status;
        setSegmentStatusByType((prev) => ({
          ...prev,
          [json.type]: status,
        }));

        if (status === 'COMPLETED') {
          // Continue checking segment completion status periodically
          // This helps catch completion when multiple tasks are running
          checkSegmentCompletion();
          return;
        } else if (status === 'FAILED') {
          setSegmentCompleted(false);
          setSegmentFailed(true);
          return;
        }
      } catch (e) {
        if (attempt === maxAttempts) {
          setSegmentStatusByType((prev) => ({
            ...prev,
            [taskType]: 'FAILED',
          }));
          setSegmentFailed(true);
          return;
        }
      }
      await new Promise((r) => setTimeout(r, interval));
    }
  };

  // Helper function to periodically check segment completion
  const checkSegmentCompletion = async () => {
    if (!currentSegmentId || !teamspaceId || !selectedLoop?.id) return;

    try {
      const segmentData = await loopDataService.fetchHref(
        `/spaces/${teamspaceId}/loops/${selectedLoop.id}/segments/${currentSegmentId}`,
      );
      if (segmentData?.completed === true) {
        // Only mark as completed if all locally-tracked task statuses
        // have also reached COMPLETED, so the success screen does not
        // appear before the progress UI has caught up.
        const allLocalTasksDone =
          Object.keys(segmentStatusByType).length > 0 &&
          Object.values(segmentStatusByType).every((s) => s === 'COMPLETED');
        if (allLocalTasksDone) {
          setSegmentCompleted(true);
          setSegmentFailed(false);
        }
      }
      if (segmentData?.failedTask) {
        setSegmentFailed(true);
      }
    } catch (e) {
      console.error('Failed to check segment completion', e);
    }
  };

  const getLatestTasksByType = (tasks: any[]) => {
    const latestByType: Record<string, any> = {};

    tasks.forEach((task) => {
      const type = task.type;
      if (!type) return;

      const current = latestByType[type];

      // If we have nothing stored yet, or this task has a newer completionDate, replace it
      if (!current) {
        latestByType[type] = task;
        return;
      }

      const prevDate = current.creationDate
        ? new Date(current.creationDate).getTime()
        : 0;
      const nextDate = task.creationDate
        ? new Date(task.creationDate).getTime()
        : 0;

      if (nextDate > prevDate) {
        latestByType[type] = task;
      }
    });

    return latestByType;
  };

  const pollTaskStatus = async (
    selfHref: string,
    taskType: string,
    displayName: string,
    maxAttempts: number = 30,
    interval: number = 3000,
  ) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const resp = await fetch(selfHref, {
          headers: {
            Authorization: 'Bearer ' + (user?.access_token || ''),
            Accept: 'application/hal+json',
          },
        });
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        const json = await resp.json();
        const status = json?.status;
        // Use provided displayName directly (avoids race with async state update)
        const baseName = displayName || taskType.replace(/_/g, ' ');
        const successMsg = `${baseName} has been added`;
        const failureMsg = `${baseName} failed`;
        if (status === 'COMPLETED') {
          showSnackbar(successMsg, 'success');
          fetchLoopResources();
          fetchLoopSegments(); // Refresh segments to update segment resources
          // Remove task from active placeholders
          setActiveResourceTasks((prev) =>
            prev.filter((t) => t.selfHref !== selfHref),
          );
          return;
        } else if (status === 'FAILED') {
          showSnackbar(failureMsg, 'error');
          setActiveResourceTasks((prev) =>
            prev.filter((t) => t.selfHref !== selfHref),
          );
          return;
        }
      } catch (e) {
        if (attempt === maxAttempts) {
          showSnackbar(
            `Polling failed for ${taskType}: ${(e as any).message}`,
            'error',
          );
          setActiveResourceTasks((prev) =>
            prev.filter((t) => t.selfHref !== selfHref),
          );
          return;
        }
      }
      await new Promise((r) => setTimeout(r, interval));
    }
    showSnackbar(`Polling timeout for ${taskType}`, 'error');
    setActiveResourceTasks((prev) =>
      prev.filter((t) => t.selfHref !== selfHref),
    );
  };

  const retrySegmentTask = async (tasksHref: string | null) => {
    setSegmentFailed(false);
    setRightPanelType('segmentProgress');
    if (tasksHref) {
      const fetchTasksResp = await loopDataService.fetchHref(tasksHref);
      const allTasks = fetchTasksResp?._embedded?.tasks ?? [];
      const latestByType = getLatestTasksByType(allTasks);
      const uniqueTasks = Object.values(latestByType);

      if (resolveIncompleteSegment) {
        // Set status by type based on status returned for each task
        uniqueTasks.forEach((task) => {
          const status = task.status;
          const type = task.type;
          if (status) {
            setSegmentStatusByType((prev) => ({
              ...prev,
              [type]: status,
            }));
          }
        });
      } else {
        // Only set QUEUED for types that are currently FAILED, keep others as they are
        setSegmentStatusByType((prev) => {
          const next = { ...prev };

          Object.keys(prev).forEach((type) => {
            if (prev[type] === 'FAILED') {
              next[type] = 'queued';
            }
          });

          return next;
        });
      }

      checkSegmentCompletion();

      for (const task of uniqueTasks) {
        pollSegmentTaskStatus(task._links?.self?.href, task.type, 30, 3000);
      }
    } else if (resolveIncompleteSegment && currentSegmentId) {
      // No tasksHref available (e.g. retry API returned 200 meaning all tasks
      // already completed). Fetch tasks directly to populate real statuses so
      // the progress UI shows COMPLETED instead of "Waiting in Queue".
      try {
        const tasks = await loopDataService.getSegmentTasks(currentSegmentId);
        const taskList = Array.isArray(tasks) ? tasks : [];
        taskList.forEach((task: any) => {
          if (task.status && task.type) {
            setSegmentStatusByType((prev) => ({
              ...prev,
              [task.type]: task.status,
            }));
          }
        });
        checkSegmentCompletion();
      } catch (e) {
        console.error('Failed to fetch segment tasks for resolve:', e);
      }
    }
    // Refresh the segments list to show the newly created segment
    fetchLoopSegments();
  };

  const handleSubmitForm = async (type: string, data: any) => {
    if (!teamspaceId || !selectedLoop?.id) {
      showSnackbar('Missing loop context', 'error');
      return;
    }
    try {
      if (type === 'project') {
        const projectName = data.projectName || data.name || 'Unnamed Project';
        const projectDescription =
          data.projectDescription || data.description || '';
        const segmentId = data.segmentId || undefined;
        const resp = await loopDataService.addTestProjectToLoop(
          teamspaceId,
          selectedLoop.id,
          projectName,
          projectDescription,
          segmentId,
        );
        const selfHref = resp?._links?.self?.href;
        // Add placeholder row instead of immediate success alert
        if (selfHref) {
          setActiveResourceTasks((prev) => [
            ...prev,
            {
              selfHref,
              taskType: 'ADD_TEST_PROJECT_TO_LOOP',
              displayName: projectName + ' Project',
            },
          ]);
          pollTaskStatus(
            selfHref,
            'ADD_TEST_PROJECT_TO_LOOP',
            projectName + ' Project',
          );
        }
      } else if (type === 'repository') {
        const repoName =
          data.repositoryName ||
          data.repoName ||
          data.name ||
          'Unnamed Repository';
        const segmentId = data.segmentId || undefined;
        const resp = await loopDataService.addControlRepoToLoop(
          teamspaceId,
          selectedLoop.id,
          repoName,
          segmentId,
        );
        const selfHref = resp?._links?.self?.href;
        if (selfHref) {
          setActiveResourceTasks((prev) => [
            ...prev,
            {
              selfHref,
              taskType: 'ADD_CONTROL_REPO_TO_LOOP',
              displayName: repoName + ' Repository',
            },
          ]);
          pollTaskStatus(
            selfHref,
            'ADD_CONTROL_REPO_TO_LOOP',
            repoName + ' Repository',
          );
        }
      } else if (type === 'segment') {
        try {
          // Reset all segment progress state before starting a new creation
          // to prevent stale state from a previous segment bleeding through
          setSegmentCompleted(null);
          setSegmentFailed(false);
          setSegmentStatusByType({});
          setCurrentSegmentId(null);

          const segmentName =
            data.segmentName || data.name || 'Unnamed Segment';
          const segmentDescription =
            data.segmentDescription || data.description || '';
          const workflowType = data.workflowType || 'default';
          const demoDataType = data.demoDataType || 'none';
          // Fetch segment task sequence
          const segmentTasks = await loopDataService.fetchSegmentTaskSequence(
            teamspaceId,
            selectedLoop.id,
          );
          const categories = Array.isArray(segmentTasks?.categories)
            ? [...segmentTasks.categories]
            : [];
          categories.sort(
            (categoryA, categoryB) =>
              (categoryA.order ?? 0) - (categoryB.order ?? 0),
          );

          const fullSequence: {
            displayName: string;
            type: string;
            description: string;
          }[] = [];
          categories.forEach((cat) => {
            const tasks = Array.isArray(cat.tasks) ? [...cat.tasks] : [];
            tasks.sort(
              (taskA, taskB) => (taskA.order ?? 0) - (taskB.order ?? 0),
            );
            tasks.forEach((task) => {
              fullSequence.push({
                displayName: task.displayName,
                type: task.type,
                description: task.description,
              });
            });
          });

          const resp = await loopDataService.addLoopSegmentToLoop(
            teamspaceId,
            selectedLoop.id,
            segmentName,
            segmentDescription,
            workflowType,
            demoDataType,
          );

          const tasksHref = resp?._links?.tasks?.href;
          const fetchTasksResp = await loopDataService.fetchHref(tasksHref);

          // Store segment ID and check completion from segment API
          const segmentId =
            resp?.id || resp?._links?.self?.href?.split('/').pop();
          setCurrentSegmentId(segmentId);

          if (segmentId) {
            try {
              const segmentData = await loopDataService.fetchHref(
                `/spaces/${teamspaceId}/loops/${selectedLoop.id}/segments/${segmentId}`,
              );
              setSegmentCompleted(segmentData?.completed === true);
            } catch (e) {
              console.error('Failed to fetch segment completion status', e);
              setSegmentCompleted(false);
            }
          }

          setEnabledTaskTypes(fullSequence);
          const types = fullSequence.map((t) => t.type);

          // initialize status map: every task starts as 'queued'
          const initialStatuses: Record<string, string | null> = types.reduce(
            (acc, type) => {
              acc[type] = 'queued';
              return acc;
            },
            {} as Record<string, string | null>,
          );
          setSegmentStatusByType(initialStatuses);
          setRightPanelType('segmentProgress');

          for (const task of fetchTasksResp._embedded.tasks) {
            pollSegmentTaskStatus(task._links?.self?.href, task.type);
          }

          // Refresh the segments list to show the newly created segment
          fetchLoopSegments();
        } catch (e: any) {
          if (e?.data?.detail) {
            setSegmentCreationError(e.data.detail);
          } else {
            setSegmentCreationError(t('failed-to-create-segment'));
          }
          // Reset panel state to prevent the segment completion polling
          // useEffect from activating indefinitely when creation failed
          // after rightPanelType was already set to 'segmentProgress'.
          setRightPanelType('segment');
          setCurrentSegmentId(null);
          setSegmentCompleted(null);
        }
      } else {
        showSnackbar('Unknown resource type', 'error');
      }
    } catch (e) {
      console.error('Failed to create resource task', e);
      showSnackbar('Failed to create resource', 'error');
    } finally {
      if (type !== 'segment') {
        handleCloseRightPanel();
      }
    }
  };

  const handleAlertClose = () => setAlertOpen(false);

  const handleIncompleteSegmentClick = async (segment: LoopSegment) => {
    setCurrentSegmentId(segment.id || null);
    // Ensure enabled task types are set:
    // Fetch segment task sequence
    const segmentTasks = await loopDataService.fetchSegmentTaskSequence(
      teamspaceId,
      selectedLoop.id,
    );
    const categories = Array.isArray(segmentTasks?.categories)
      ? [...segmentTasks.categories]
      : [];
    categories.sort(
      (categoryA, categoryB) => (categoryA.order ?? 0) - (categoryB.order ?? 0),
    );

    const fullSequence: {
      displayName: string;
      type: string;
      description: string;
    }[] = [];
    categories.forEach((cat) => {
      const tasks = Array.isArray(cat.tasks) ? [...cat.tasks] : [];
      tasks.sort((taskA, taskB) => (taskA.order ?? 0) - (taskB.order ?? 0));
      tasks.forEach((task) => {
        fullSequence.push({
          displayName: task.displayName,
          type: task.type,
          description: task.description,
        });
      });
    });
    setEnabledTaskTypes(fullSequence);
    setRightPanelType('segmentProgress');
    setRightPanelOpen(true);
    setResolveIncompleteSegment(true);
  };

  return (
    <div
      id="id-loop-details-page-container"
      data-testid="loop-details-page-container"
      className="min-h-screen bg-gray-50 dark:bg-carbon-dark"
    >
      {/* Page Title */}
      <div className="flex min-h-14 flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-carbon-darkSurface">
        <h1
          id="id-loop-details-page-title"
          data-testid="loop-details-page-title"
          className="font-roboto text-xl font-bold leading-tight text-breadcrumb-title dark:text-gray-100"
        >
          {selectedLoop?.name || t('loop')} {t('details')}
        </h1>
        {alertOpen && (
          <div
            id="id-loop-details-alert-box"
            data-testid="loop-details-alert-box"
            className={`flex w-full max-w-[600px] items-center justify-between rounded border-y border-l-4 border-r p-1.5 font-inter text-xs shadow-sm ${
              alertType === 'success'
                ? 'border-status-successBorder bg-status-successBg dark:border-green-600 dark:bg-green-950'
                : 'border-status-failBorder bg-status-failBg dark:border-red-600 dark:bg-red-950'
            } text-foreground dark:text-gray-100`}
          >
            <span
              id="id-loop-details-alert-icon"
              data-testid="loop-details-alert-icon"
              className="mr-2 flex"
            >
              <img
                src={
                  alertType === 'success' ? successAlertIcon : errorAlertIcon
                }
                alt={alertType === 'success' ? 'Success' : 'Error'}
                className="size-4"
              />
            </span>
            <span
              id="id-loop-details-alert-message"
              data-testid="loop-details-alert-message"
              className="flex-1"
            >
              {alertMessage}
            </span>
            <button
              onClick={handleAlertClose}
              className="ml-2.5 cursor-pointer border-none bg-transparent text-base text-foreground"
              aria-label="Close alert"
            >
              ✖
            </button>
          </div>
        )}
      </div>
      <div className="px-8 pb-8">
        <div
          id="id-loop-details-card"
          data-testid="loop-details-card"
          className="mt-6 rounded-lg bg-card p-6 shadow-sm"
        >
          <div
            id="id-loop-details-tab-bar"
            data-testid="loop-details-tab-bar"
            className="border-b border-border"
          >
            <div
              id="id-loop-details-tab-header"
              data-testid="loop-details-tab-header"
              className="flex items-center justify-between"
            >
              <div
                id="id-loop-details-tab-list"
                data-testid="loop-details-tab-list"
                className="flex gap-8"
              >
                <button
                  onClick={() => setActiveTab('members')}
                  className={`relative px-1 pb-3 text-sm font-medium transition-colors ${
                    activeTab === 'members'
                      ? 'text-brand-interactive'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span
                    id="id-loop-details-members-tab-label"
                    data-testid="loop-details-members-tab-label"
                    className="flex items-center gap-2"
                  >
                    <UserMultiple size={16} />
                    {t('team-members')}
                  </span>
                  {activeTab === 'members' && (
                    <div
                      id="id-loop-details-members-tab-indicator"
                      data-testid="loop-details-members-tab-indicator"
                      className="absolute inset-x-0 bottom-0 h-[2px] bg-brand-interactive"
                    ></div>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('connections')}
                  className={`relative px-1 pb-3 text-sm font-medium transition-colors ${
                    activeTab === 'connections'
                      ? 'text-brand-interactive'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span
                    id="id-loop-details-connections-tab-label"
                    data-testid="loop-details-connections-tab-label"
                    className="flex items-center gap-2"
                  >
                    <Connect size={16} />
                    {t('loop-connections')}
                  </span>
                  {activeTab === 'connections' && (
                    <div
                      id="id-loop-details-connections-tab-indicator"
                      data-testid="loop-details-connections-tab-indicator"
                      className="absolute inset-x-0 bottom-0 h-[2px] bg-brand-interactive"
                    ></div>
                  )}
                </button>

                {/* Webhooks tab — admin only (Loop / Teamspace Admin) */}
                {isTeamspaceAdmin && (
                  <button
                    onClick={() => setActiveTab('webhooks')}
                    className={`relative px-1 pb-3 text-sm font-medium transition-colors ${
                      activeTab === 'webhooks'
                        ? 'text-brand-interactive'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span
                      id="id-loop-details-webhooks-tab-label"
                      data-testid="loop-details-webhooks-tab-label"
                      className="flex items-center gap-2"
                    >
                      <EventSchedule size={16} />
                      {t('webhooks')}
                    </span>
                    {activeTab === 'webhooks' && (
                      <div
                        id="id-loop-details-webhooks-tab-indicator"
                        data-testid="loop-details-webhooks-tab-indicator"
                        className="absolute inset-x-0 bottom-0 h-[2px] bg-brand-interactive"
                      ></div>
                    )}
                  </button>
                )}
                {loopHasWiki !== false && (
                  <button
                    onClick={() => setActiveTab('wiki')}
                    className={`relative px-1 pb-3 text-sm font-medium transition-colors ${
                      activeTab === 'wiki'
                        ? 'text-brand-interactive'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span
                      id="id-loop-details-wiki-tab-label"
                      data-testid="loop-details-wiki-tab-label"
                      className="flex items-center gap-2"
                    >
                      {t('wiki.tabLabel')}
                    </span>
                    {activeTab === 'wiki' && (
                      <div
                        id="id-loop-details-wiki-tab-indicator"
                        data-testid="loop-details-wiki-tab-indicator"
                        className="absolute inset-x-0 bottom-0 h-[2px] bg-brand-interactive"
                      ></div>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
          {activeTab === 'connections' && (
            <div
              id="id-loop-details-connections-content"
              data-testid="loop-details-connections-content"
              className="mt-6"
            >
              {/* Connected Apps Section */}
              <div
                id="id-loop-details-connected-apps-section"
                data-testid="loop-details-connected-apps-section"
                className="mb-8"
              >
                <div
                  id="id-loop-details-connected-apps-header"
                  data-testid="loop-details-connected-apps-header"
                  className="mb-4 flex items-center justify-between"
                >
                  <h2 className="text-base font-medium text-foreground">
                    {t('connected-apps')}
                  </h2>
                  {renderActionButton()}
                </div>
                <div
                  id="id-loop-details-connected-apps-table-wrapper"
                  data-testid="loop-details-connected-apps-table-wrapper"
                  className="overflow-hidden rounded border border-border"
                >
                  <table className="w-full">
                    <thead className="bg-secondary/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          {t('solution', 'Solution')}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          {t('loop', 'Loop')}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          {t('resource', 'Resource')}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          {t('created-by', 'Created By')}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                          {t('date', 'Date')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {resourcesLoading &&
                        Array.from({ length: 5 }).map((_, index) => (
                          <tr
                            key={`skeleton-${index}`}
                            className="border-b border-border"
                          >
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-resource-skeleton-solution"
                                data-testid="loop-details-resource-skeleton-solution"
                                className="h-4 w-16 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-resource-skeleton-loop"
                                data-testid="loop-details-resource-skeleton-loop"
                                className="h-4 w-40 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-resource-skeleton-resource"
                                data-testid="loop-details-resource-skeleton-resource"
                                className="h-4 w-24 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-resource-skeleton-created-by"
                                data-testid="loop-details-resource-skeleton-created-by"
                                className="h-4 w-28 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-resource-skeleton-date"
                                data-testid="loop-details-resource-skeleton-date"
                                className="h-4 w-28 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                          </tr>
                        ))}
                      {/* Active task placeholder rows */}
                      {activeResourceTasks.map((task) => (
                        <tr
                          key={task.selfHref}
                          className="border-b border-border"
                        >
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-task-skeleton-solution"
                              data-testid="loop-details-task-skeleton-solution"
                              className="h-4 w-16 animate-pulse rounded bg-muted"
                            ></div>
                          </td>
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-task-skeleton-loop"
                              data-testid="loop-details-task-skeleton-loop"
                              className="h-4 w-40 animate-pulse rounded bg-muted"
                            ></div>
                          </td>
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-task-skeleton-resource"
                              data-testid="loop-details-task-skeleton-resource"
                              className="h-4 w-24 animate-pulse rounded bg-muted"
                            ></div>
                          </td>
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-task-skeleton-created-by"
                              data-testid="loop-details-task-skeleton-created-by"
                              className="h-4 w-28 animate-pulse rounded bg-muted"
                            ></div>
                          </td>
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-task-skeleton-date"
                              data-testid="loop-details-task-skeleton-date"
                              className="h-4 w-28 animate-pulse rounded bg-muted"
                            ></div>
                          </td>
                        </tr>
                      ))}
                      {!resourcesLoading &&
                        loopResources.length === 0 &&
                        activeResourceTasks.length === 0 && (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-4 py-8 text-center text-sm text-muted-foreground"
                            >
                              {t('no-resources-found', 'No resources found')}
                            </td>
                          </tr>
                        )}
                      {!resourcesLoading &&
                        loopResources.map((res, idx) => (
                          <tr
                            key={res.id || idx}
                            className={`transition-colors hover:bg-secondary ${idx !== loopResources.length - 1 ? 'border-b border-border' : ''}`}
                          >
                            <td className="px-4 py-3 text-sm text-secondary-foreground">
                              {mapResourceType(res.type)}
                            </td>
                            <td className="px-4 py-3 text-sm text-secondary-foreground">
                              {formatResourceName(res)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {res.link ? (
                                hasLicenseForResource(res.type) ? (
                                  <a
                                    href={buildResourceLink(res.link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:underline"
                                  >
                                    {t('view-resource')}
                                  </a>
                                ) : (
                                  <span
                                    id="id-loop-details-resource-no-license"
                                    data-testid="loop-details-resource-no-license"
                                    className="cursor-not-allowed text-muted-foreground opacity-50"
                                  >
                                    {t('view-resource')}
                                  </span>
                                )
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-secondary-foreground">
                              {res.creator?.fullName || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-secondary-foreground">
                              {res.created ? formatDate(res.created) : ''}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Loop Segments Section */}
              <div
                id="id-loop-details-segments-section"
                data-testid="loop-details-segments-section"
              >
                <div
                  id="id-loop-details-segments-header"
                  data-testid="loop-details-segments-header"
                  className="mb-4 flex items-center gap-2"
                >
                  <h2 className="text-base font-medium text-foreground">
                    {t('loop-segments', 'Loop Segments')}
                  </h2>
                  <span
                    id="id-loop-details-segments-count-badge"
                    data-testid="loop-details-segments-count-badge"
                    className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground"
                  >
                    {segmentsWithTasks.length}
                  </span>
                </div>

                {segmentsLoading && (
                  <div
                    id="id-loop-details-segments-skeleton-table"
                    data-testid="loop-details-segments-skeleton-table"
                    className="overflow-hidden rounded border border-border"
                  >
                    <table className="w-full">
                      <thead className="bg-secondary/50">
                        <tr>
                          {[
                            t('solution', 'Solution'),
                            t('loop', 'Loop'),
                            t('resource', 'Resource'),
                            t('created-by', 'Created By'),
                            t('date', 'Date'),
                          ].map((header) => (
                            <th
                              key={header}
                              className="px-4 py-3 text-left text-sm font-medium text-foreground"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: 3 }).map((_, index) => (
                          <tr
                            key={`segment-skeleton-${index}`}
                            className="border-b border-border"
                          >
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-segment-skeleton-solution"
                                data-testid="loop-details-segment-skeleton-solution"
                                className="h-4 w-16 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-segment-skeleton-loop"
                                data-testid="loop-details-segment-skeleton-loop"
                                className="h-4 w-40 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-segment-skeleton-resource"
                                data-testid="loop-details-segment-skeleton-resource"
                                className="h-4 w-24 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-segment-skeleton-created-by"
                                data-testid="loop-details-segment-skeleton-created-by"
                                className="h-4 w-28 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-segment-skeleton-date"
                                data-testid="loop-details-segment-skeleton-date"
                                className="h-4 w-28 animate-pulse rounded bg-muted"
                              ></div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {!segmentsLoading && segmentsError && (
                  <div
                    id="id-loop-details-segments-error"
                    data-testid="loop-details-segments-error"
                    className="p-4 text-sm text-destructive"
                  >
                    {segmentsError}
                  </div>
                )}

                {!segmentsLoading &&
                  !segmentsError &&
                  segmentsWithTasks.length === 0 && (
                    <div
                      id="id-loop-details-no-segments"
                      data-testid="loop-details-no-segments"
                      className="p-4 text-center text-sm text-muted-foreground"
                    >
                      {t('no-segments-found', 'No segments found')}
                    </div>
                  )}

                {!segmentsLoading &&
                  !segmentsError &&
                  segmentsWithTasks
                    .slice(0, visibleSegmentCount)
                    .map((segment, idx) => {
                      const segKey =
                        segment.id ||
                        segment.displayName ||
                        segment.name ||
                        `${idx}`;
                      const isExpanded = expandedSegments[segKey] !== false;
                      return (
                        <div
                          id="id-loop-details-segment-item"
                          data-testid="loop-details-segment-item"
                          key={segment.id || idx}
                          className={
                            idx < segmentsWithTasks.length - 1 ? 'mb-4' : ''
                          }
                        >
                          <button
                            onClick={() => toggleSegment(segKey)}
                            className="mb-2 flex items-center gap-2 text-base text-foreground transition-colors hover:text-primary"
                          >
                            {isExpanded ? (
                              <ExpandMoreIcon className="text-base" />
                            ) : (
                              <ChevronRightIcon className="text-base" />
                            )}
                            <span
                              id="id-loop-details-segment-name"
                              data-testid="loop-details-segment-name"
                            >
                              {segment.displayName ||
                                segment.name ||
                                `${t('segment')} ${idx + 1}`}
                            </span>
                            {!segment.completed && (
                              <>
                                <WarningIcon
                                  sx={{
                                    color: '#EB0909',
                                    width: '20px',
                                    height: '20px',
                                    verticalAlign: 'top',
                                  }}
                                  fontSize="inherit"
                                />
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="cursor-pointer text-sm text-primary hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleIncompleteSegmentClick(segment);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.stopPropagation();
                                      handleIncompleteSegmentClick(segment);
                                    }
                                  }}
                                >
                                  {t('resolve')}
                                </span>
                              </>
                            )}
                          </button>

                          {isExpanded && (
                            <div
                              id="id-loop-details-segment-resources-table"
                              data-testid="loop-details-segment-resources-table"
                              className="ml-6 overflow-hidden rounded border border-border"
                            >
                              <table className="w-full">
                                <thead className="bg-secondary/50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                                      {t('solution', 'Solution')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                                      {t('loop', 'Loop')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                                      {t('resource', 'Resource')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                                      {t('created-by', 'Created By')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                                      {t('date', 'Date')}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {segment.resources &&
                                  segment.resources.length > 0 ? (
                                    segment.resources.map(
                                      (
                                        resource: LoopResource,
                                        resourceIdx: number,
                                      ) => (
                                        <tr
                                          key={resource.id || resourceIdx}
                                          className={`transition-colors hover:bg-secondary ${resourceIdx !== (segment.resources?.length ?? 0) - 1 ? 'border-b border-border' : ''}`}
                                        >
                                          <td className="px-4 py-3 text-sm text-secondary-foreground">
                                            {mapResourceType(resource.type)}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-secondary-foreground">
                                            {formatResourceName(resource)}
                                          </td>
                                          <td className="px-4 py-3 text-sm">
                                            {resource.link ? (
                                              hasLicenseForResource(
                                                resource.type,
                                              ) ? (
                                                <a
                                                  href={buildResourceLink(
                                                    resource.link,
                                                  )}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-primary hover:underline"
                                                >
                                                  {t('view-resource')}
                                                </a>
                                              ) : (
                                                <span
                                                  id="id-loop-details-segment-resource-no-license"
                                                  data-testid="loop-details-segment-resource-no-license"
                                                  className="cursor-not-allowed text-muted-foreground opacity-50"
                                                >
                                                  {t('view-resource')}
                                                </span>
                                              )
                                            ) : (
                                              '-'
                                            )}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-secondary-foreground">
                                            {segment.creatorUsername || '-'}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-secondary-foreground">
                                            {resource.created
                                              ? new Date(
                                                  resource.created,
                                                ).toLocaleDateString()
                                              : '-'}
                                          </td>
                                        </tr>
                                      ),
                                    )
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan={5}
                                        className="px-4 py-8 text-center text-sm text-muted-foreground"
                                      >
                                        {t(
                                          'no-resources-found',
                                          'No resources found',
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}

                {/* Sentinel div for infinite scroll */}
                {visibleSegmentCount < segmentsWithTasks.length && (
                  <div
                    id="id-loop-details-scroll-sentinel"
                    data-testid="loop-details-scroll-sentinel"
                    ref={segmentSentinelRef}
                    className="flex min-h-6 w-full justify-center py-3"
                  >
                    <span
                      id="id-loop-details-loading-more-text"
                      data-testid="loop-details-loading-more-text"
                      className="text-xs text-muted-foreground"
                    >
                      {loadingMoreSegments ? t('Loading more...') : '\u00A0'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div
              id="id-loop-details-members-content"
              data-testid="loop-details-members-content"
              className="mt-6 flex flex-col gap-4"
            >
              {/* Header with member count and Edit Members button */}
              <div
                id="id-loop-details-members-header"
                data-testid="loop-details-members-header"
                className="flex items-center justify-between"
              >
                <div
                  id="id-loop-details-members-title-group"
                  data-testid="loop-details-members-title-group"
                  className="flex items-center gap-2"
                >
                  <h2 className="text-base font-medium text-foreground">
                    {t('team-members')}
                  </h2>
                  <span
                    id="id-loop-details-members-count-badge"
                    data-testid="loop-details-members-count-badge"
                    className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground"
                  >
                    {
                      rawLoopMembers.filter((member) => {
                        if (!searchQuery.trim()) return true;
                        const q = searchQuery.toLowerCase();
                        return (
                          (member.fullName || '').toLowerCase().includes(q) ||
                          (member.email || '').toLowerCase().includes(q) ||
                          (member.userId || '').toLowerCase().includes(q)
                        );
                      }).length
                    }
                  </span>
                </div>
                {isTeamspaceAdmin && (
                  <button
                    onClick={async () => {
                      await refreshLoopData();
                      await prepareModalMembers();
                      setEditModalOpen(true);
                    }}
                    className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground transition-colors hover:bg-primary-hover"
                  >
                    {t('edit-members')}
                  </button>
                )}
              </div>

              {/* Team Members Table */}
              <div
                id="id-loop-details-members-table-wrapper"
                data-testid="loop-details-members-table-wrapper"
                className="overflow-hidden rounded border border-border"
              >
                <table className="w-full">
                  <thead className="bg-secondary/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground"></th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                        {t('name')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                        {t('role')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                        {t('email')}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                        {t('loops')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersLoading &&
                      Array.from({ length: 5 }).map((_, index) => (
                        <tr
                          key={`member-skeleton-${index}`}
                          className="border-b border-border"
                        >
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-member-avatar-skeleton"
                              data-testid="loop-details-member-avatar-skeleton"
                              className="size-8 animate-pulse rounded-full bg-muted"
                            ></div>
                          </td>
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-member-skeleton-name"
                              data-testid="loop-details-member-skeleton-name"
                              className="h-4 w-24 animate-pulse rounded bg-muted"
                            ></div>
                          </td>
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-member-skeleton-role"
                              data-testid="loop-details-member-skeleton-role"
                              className="h-4 w-20 animate-pulse rounded bg-muted"
                            ></div>
                          </td>
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-member-skeleton-email"
                              data-testid="loop-details-member-skeleton-email"
                              className="h-4 w-32 animate-pulse rounded bg-muted"
                            ></div>
                          </td>
                          <td className="px-4 py-3">
                            <div
                              id="id-loop-details-member-skeleton-loops"
                              data-testid="loop-details-member-skeleton-loops"
                              className="h-4 w-24 animate-pulse rounded bg-muted"
                            ></div>
                          </td>
                        </tr>
                      ))}
                    {!membersLoading && membersError && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-3 text-center text-sm text-destructive"
                        >
                          {membersError}
                        </td>
                      </tr>
                    )}
                    {!membersLoading &&
                      !membersError &&
                      rawLoopMembers.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-3 text-center text-sm text-gray-500"
                          >
                            {t('no-members')}
                          </td>
                        </tr>
                      )}
                    {!membersLoading &&
                      !membersError &&
                      rawLoopMembers
                        .filter((member) => {
                          if (!searchQuery.trim()) return true;
                          const q = searchQuery.toLowerCase();
                          return (
                            (member.fullName || '').toLowerCase().includes(q) ||
                            (member.email || '').toLowerCase().includes(q) ||
                            (member.userId || '').toLowerCase().includes(q)
                          );
                        })
                        .map((member, idx) => (
                          <tr
                            key={member.userId || idx}
                            className="border-b border-border transition-colors hover:bg-secondary"
                          >
                            <td className="px-4 py-3">
                              <div
                                id="id-loop-details-member-avatar"
                                data-testid="loop-details-member-avatar"
                                className="flex size-8 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground"
                              >
                                {(member.fullName || member.email || '?')
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-secondary-foreground">
                              {member.fullName || member.email}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-secondary-foreground">
                              {mapRole(member.role)}
                            </td>
                            <td className="px-4 py-3 text-sm text-secondary-foreground">
                              {member.email}
                            </td>
                            <td className="px-4 py-3 text-sm text-secondary-foreground">
                              {member.otherLoops?.length
                                ? member.otherLoops.join(', ')
                                : '-'}
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Webhooks tab — admin only (Loop / Teamspace Admin) */}
          {activeTab === 'webhooks' && isTeamspaceAdmin && (
            <WebhooksTab
              loopId={selectedLoop?.id || loopId || ''}
              teamspaceId={teamspaceId || ''}
            />
          )}
          {activeTab === 'wiki' && (
            <WikiTab
              controlBasePath={controlBasePath}
              teamspaceName={teamspaceName ?? ''}
              loopName={loopName}
              hasWiki={loopHasWiki}
            />
          )}
        </div>

        {/* Right Panel Component */}
        <RightPanel
          open={rightPanelOpen}
          onClose={handleCloseRightPanel}
          panelType={rightPanelType}
          onSubmit={handleSubmitForm}
          existingResources={loopResources}
          enabledTaskTypes={enabledTaskTypes}
          segmentCompleted={segmentCompleted}
          loopSegments={loopSegments}
          currentLoopId={selectedLoop?.id}
          currentLoopName={selectedLoop?.name}
          retrySegmentTask={retrySegmentTask}
          segmentId={currentSegmentId}
          teamspaceId={teamspaceId || ''}
          loopId={selectedLoop?.id}
          segmentStatusByType={segmentStatusByType}
          resolveIncompleteSegment={resolveIncompleteSegment}
          setResolveIncompleteSegment={setResolveIncompleteSegment}
          segmentFailed={segmentFailed}
          segmentCreationError={segmentCreationError}
          setSegmentCreationError={setSegmentCreationError}
          setSegmentFailed={setSegmentFailed}
        />

        {/* Edit Modal Component */}
        <EditModal
          isVisible={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          loopName={selectedLoop?.name}
          loopId={selectedLoop?.id}
          associatedUsers={loopAssociatedUsers}
          refreshLoops={refreshLoopData}
          showSnackbar={showSnackbar}
        />

        {/* Custom alert replaces Snackbar */}
      </div>
    </div>
  );
};

export default LoopDetailsPage;
